import React, { useState, useEffect } from "react";
import { DialogBox } from "@/components/common/DialogBox";
import ReferralsIcon from "@/assets/svg/referralsLightIcon.svg";
import SearchDialogBox from "@/components/common/SearchDialogBox";
import { Typography } from "@/components/ui/typography";
import api from "@/axios";
import { routes } from "@/utils/urls";
import { DataTable } from "@/components/common/Table";
import DialogDropdown from "@/components/common/DialogDropdown";
import { columns as referralsColumns } from "./referralsColumn";
import { useDebounce } from "@/hooks/useDebounce";
import {
  applyTimeRangeSelection,
  normalizeListResponse,
} from "../common/dialogUtils";

interface ReferralsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
}

interface Referral {
  id: number;
  referralId: string | number;
  referralDate: string;
  sendingFacility: string;
  receivingFacility: string;
  referralStatus: string;
  referralType: string;
}

const ReferralsDialog: React.FC<ReferralsDialogProps> = ({
  open,
  onOpenChange,
  memberId,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [, setReferrals] = useState<Referral[]>([]);
  const [allReferrals, setAllReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [error, setError] = useState("");
  const [, setPage] = useState(1);

  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  const [timeRange, setTimeRange] = useState("all");
  const debouncedSearchValue = useDebounce(searchValue, 1000); // Debounce search value

  const rowsPerPage = 20;

  const timeRangeOptions = [
    { label: "All Time", value: "all" },
    { label: "1 Month", value: "30" },
    { label: "2 Months", value: "60" },
    { label: "3 Months", value: "90" },
    { label: "4 Months", value: "120" },
    { label: "5 Months", value: "150" },
    { label: "6 Months", value: "180" },
    { label: "7 Months", value: "210" },
    { label: "8 Months", value: "240" },
    { label: "9 Months", value: "270" },
    { label: "10 Months", value: "300" },
    { label: "11 Months", value: "330" },
    { label: "12 Months", value: "365" },
  ];

  const handleTimeRangeChange = (data: {
    days?: string;
    value?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    applyTimeRangeSelection(data, setTimeRange, setStartDate, setEndDate);

  const fetchReferrals = async (
    pageNumber: number = 1,
    append: boolean = false
  ) => { // NOSONAR
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
        setAllReferrals([]);
        setPage(1);
        setHasMoreData(true);
      }
      setError("");

      const url = routes.cards.referrals(memberId);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const requestBody: any = {
        view: "list",
        searchKey: debouncedSearchValue.trim() || undefined, // Use debounced value
        page: pageNumber,
        limit: rowsPerPage,
      };

      if (timeRange !== "all") {
        requestBody.days = timeRange;
        requestBody.startDate = startDate || undefined;
        requestBody.endDate = endDate || undefined;
      }

      const res = await api.post(url, requestBody);

      const rawData = normalizeListResponse<Record<string, unknown>>(res.data);

      const transformedData: Referral[] = rawData.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any, index: number) => ({
          id: (pageNumber - 1) * rowsPerPage + index + 1,
          referralId: (item.referralId ?? item.referral_id ?? "") as
            | string
            | number,
          referralDate: item.referralDate || item.referral_date || item.date,
          sendingFacility:
            item.sendingFacility ||
            item.sending_facility ||
            item.sendingFacilityName,
          receivingFacility:
            item.receivingFacility ||
            item.receiving_facility ||
            item.receivingFacilityName,
          referralStatus:
            item.referralStatus || item.referral_status || item.status,
          referralType:
            item.referralType || item.referral_type || "",
        })
      );

      if (append) {
        setAllReferrals((prev) => [...prev, ...transformedData]);
      } else {
        setAllReferrals(transformedData);
        setReferrals(transformedData);
      }

      setHasMoreData(transformedData.length === rowsPerPage);

      setPage(pageNumber);
    } catch (err) {
      console.error("API Error fetching referrals:", err);
      // const error = err as { response?: { data?: { message?: string } }; message?: string };
      // const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch referrals.";
      // setError(errorMessage);
      setHasMoreData(false);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreData = async () => {
    if (!hasMoreData || isLoadingMore) return;
    const nextPage = Math.floor(allReferrals.length / rowsPerPage) + 1;
    await fetchReferrals(nextPage, true);
  };

  const handleScroll = ({
    scrollTop,
    scrollHeight,
    clientHeight,
  }: {
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
  }) => {
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;
    if (isNearBottom && hasMoreData && !isLoadingMore && !loading) {
      loadMoreData();
    }
  };

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setTimeRange("all");
      setStartDate(undefined);
      setEndDate(undefined);
      setAllReferrals([]);
      setPage(1);
      setHasMoreData(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fetchReferrals(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, timeRange, startDate, endDate, debouncedSearchValue]);

  return (
    <DialogBox
      open={open}
      onOpenChange={onOpenChange}
      icon={ReferralsIcon}
      iconAlt='Referrals'
      title='Referrals'>
      <div className='min-h-[500px]'>
        <div className='mb-4 space-y-3'>
          <div className='flex items-center justify-between gap-3'>
            <SearchDialogBox
              value={searchValue}
              onChange={setSearchValue}
              placeholder='Search by Sending or Receiving Facility Name'
              width='368px'
            />

            <div style={{ width: "180px", height: "40px" }}>
              <DialogDropdown
                options={timeRangeOptions}
                value={timeRange}
                onChange={handleTimeRangeChange}
                icon='calendar-line'
              />
            </div>
          </div>

          {error && (
            <Typography
              variant='medium-14'
              className='text-red-600 dark:text-red-400 font-medium'>
              {error}
            </Typography>
          )}

          <div className='overflow-x-auto sm:overflow-x-visible -mx-6 px-6 sm:mx-0 sm:px-0'>
            <div className='min-w-[800px] sm:min-w-0'>
              <div className='border rounded-lg'>
                <DataTable<Referral>
                  data={{ data: allReferrals, total: allReferrals.length }}
                  columns={referralsColumns}
                  height={allReferrals.length === 0 ? "440px" : "auto"}
                  maxHeight={"440px"}
                  isLoading={loading}
                  isLoadingMore={isLoadingMore}
                  hasMoreData={hasMoreData}
                  allData={allReferrals}
                  onScroll={handleScroll}
                  skeletonCount={10}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DialogBox>
  );
};

export default ReferralsDialog;
